import React, { useEffect, useState } from 'react'
import { useRetorikStore } from '../Contexts/RetorikContext'
import { useLocaleStore } from '../Contexts/LocaleContext'
import { useUtilsStore } from '../Contexts/UtilsContext'

import { RetorikLanguageData, RetorikMainComponentConfiguration, RetorikMainComponentProps, RetorikOtherData } from '../../models/retorikTypes'
import { useViewStore } from '../Contexts/ViewContext'

import { AvailableViews, ContainerParent, Theme } from '../../models/enums'
import { DEFAULT_VIEWS } from '../../models/constants'
import getThemeColors, { deepMerge } from '../../utils/getThemeColors'
import { fetchEmergencyData } from '../../utils/fetchEmergencyData'
import { getRetorikGDPR } from '../../utils/getGDPR'
import getRetorikConfigs from '../../utils/getConfigs'
import { EmergencyTypes } from '../../models/emergencyTypes'

import RetorikContainer from './RetorikContainer'
import Loader from '../Loader/Loader'
import getBaseAddress from '../../utils/getBaseAddress'
import { processLanguageData } from '../../utils/fetchAvailableLanguages'

interface RetorikConfigHandlerProps extends RetorikMainComponentProps {
  parent: number
}

const RetorikConfigHandler = ({
  config,
  viewsConfig,
  chatbotData,
  chatbotDataWidget,
  agentData,
  ponyfillFactoryCredentials,
  customVoice,
  colors,
  skipLoader,
  defaultMode,
  GDPR,
  customScripts,
  customCSS,
  addressData,
  externalEventHandler,
  externalComponents,
  userData,
  width,
  height,
  menuConfig,
  environmentPreset,
  parent
}: RetorikConfigHandlerProps) => {
  const { fetchSupportedLanguages, setLanguageData } = useLocaleStore((state) => state.actions)
  const setTheme = useViewStore((state) => state.actions.setTheme)
  const baseAddress = useRetorikStore((state) => state.baseAddress)
  const [fetchedConfig, setFetchedConfig] = useState<RetorikMainComponentConfiguration & RetorikLanguageData & RetorikOtherData>()
  const [mainConfig, setMainConfig] = useState<RetorikMainComponentConfiguration | undefined>(undefined)
  const { setGDPRDisplay, setGDPRUserConsent, setGDPRWindowData } = useUtilsStore((state) => state.actions)
  const { setBaseAddress, setDisableHistory, setEnvironmentPreset, setCustomScripts, setCustomCSS, setEnabledFeatures } = useRetorikStore((state) => state.actions)

  const [isConfigUpdated, setIsConfigUpdated] = useState<boolean>(false)

  const fetchConfigurationFromRetorikStudio = async (baseAddress: string): Promise<void> => {
    const fetchedConfig = await getRetorikConfigs(
      {
        config,
        viewsConfig,
        chatbotData,
        chatbotDataWidget,
        agentData,
        ponyfillFactoryCredentials,
        customVoice,
        colors,
        skipLoader,
        defaultMode,
        GDPR,
        addressData,
        externalEventHandler,
        externalComponents,
        userData,
        width,
        height,
        menuConfig
      },
      baseAddress
    )
    console.log('Retorik Framework > configuration fetched from Retorik Studio')
    fetchedConfig && setFetchedConfig(fetchedConfig)
  }

  const checkGDPR = async (baseAddress: string): Promise<void> => {
    const data = await getRetorikGDPR({
      configurationData: GDPR,
      baseAddress: baseAddress
    })

    // Set GDPR data in UtilsContext
    data.data && setGDPRWindowData(data.data)
    setGDPRDisplay(data.displayGDPR)
    typeof data.userConsent === 'boolean' && setGDPRUserConsent(data.userConsent)
  }

  /**
   * Process addressData to get the baseAddress and then :
   * - fetch configuration and save it
   * - get GDPR data and store them
   * - fetch available languages and store them
   * - set baseAddress in store
   */
  useEffect(() => {
    if (addressData && (addressData.baseURI || addressData.tenant)) {
      const baseAddress = getBaseAddress(addressData)
      fetchConfigurationFromRetorikStudio(baseAddress)
      checkGDPR(baseAddress)

      setBaseAddress(baseAddress)
    }
  }, [addressData])

  useEffect(() => {
    if (environmentPreset) {
      setEnvironmentPreset(environmentPreset)
    }
  }, [environmentPreset])

  useEffect(() => {
    if (fetchedConfig) {
      const {
        skipLoader: _skipLoader,
        defaultMode: _defaultMode,
        config: _config,
        viewsConfig: _viewsConfig,
        chatbotData: _chatbotData,
        chatbotDataWidget: _chatbotDataWidget,
        agentData: _agentData,
        addressData: _addressData,
        ponyfillFactoryCredentials: _ponyfillFactoryCredentials,
        customVoice: _customVoice,
        colors: _colors,
        defaultCulture,
        supportedCultures,
        disableHistory,
        customScripts: _customScripts,
        customCSS: _customCSS,
        features
      } = fetchedConfig

      // Language
      if (defaultCulture && supportedCultures) {
        const { processedDefaultLanguage, processedSupported } = processLanguageData(defaultCulture, supportedCultures, true)
        processedDefaultLanguage && processedSupported && setLanguageData(processedDefaultLanguage, processedSupported)
      } else {
        fetchSupportedLanguages(baseAddress)
      }

      // Colors
      if (colors?.theme || _colors?.theme) {
        const tempTheme = colors?.theme || _colors?.theme
        if (tempTheme === Theme.auto && window.matchMedia) {
          // Use window.matchMedia to detect navigator's theme and set final theme instead of "auto"
          const theme = window.matchMedia('(prefers-color-scheme:light)')?.matches ? Theme.light : Theme.dark
          setTheme(theme)
          colors && (colors.theme = theme)
          _colors && (_colors.theme = theme)
        } else {
          setTheme(tempTheme === Theme.light ? Theme.light : Theme.dark)
        }
      } else if (window.matchMedia) {
        const tempTheme = window.matchMedia('(prefers-color-scheme:light)')?.matches ? Theme.light : Theme.dark
        setTheme(tempTheme)
        _colors && (_colors.theme = tempTheme)
      }

      const mergedMainConfig: RetorikMainComponentConfiguration = {
        config: config !== undefined ? deepMerge(config, _config) : _config,
        viewsConfig: viewsConfig !== undefined ? deepMerge(viewsConfig, _viewsConfig) : _viewsConfig,
        chatbotData: chatbotData !== undefined ? { ..._chatbotData, ...chatbotData } : _chatbotData,
        chatbotDataWidget:
          chatbotDataWidget !== undefined
            ? {
                ..._chatbotDataWidget,
                ...chatbotDataWidget
              }
            : _chatbotDataWidget,
        agentData: agentData !== undefined ? (typeof agentData === 'string' ? agentData : deepMerge(agentData, _agentData)) : _agentData,
        ponyfillFactoryCredentials: ponyfillFactoryCredentials !== undefined ? ponyfillFactoryCredentials : _ponyfillFactoryCredentials,
        customVoice: customVoice !== undefined ? { ..._customVoice, ...customVoice } : _customVoice,
        colors: colors !== undefined ? deepMerge(colors, getThemeColors(_colors)) : getThemeColors(_colors),
        skipLoader: skipLoader !== undefined ? skipLoader : _skipLoader,
        defaultMode: defaultMode !== undefined ? defaultMode : _defaultMode,
        // Data not retrieved from Retorik Studio
        addressData: _addressData,
        userData: userData,
        externalEventHandler: externalEventHandler,
        externalComponents: externalComponents,
        width: width,
        height: height
      }

      // Prevent menus from being copied again after each modification of configuration
      if (mergedMainConfig.config) {
        mergedMainConfig.config.baseMenu = config?.baseMenu || menuConfig?.baseMenu
        mergedMainConfig.config.customMenu = config?.customMenu || menuConfig?.customMenu
        mergedMainConfig.config.subMenu = config?.subMenu || menuConfig?.subMenu
        mergedMainConfig.config.menu = config?.menu || menuConfig?.menu || _config?.menu
      }

      setDisableHistory(!!disableHistory)
      setCustomScripts(customScripts || _customScripts || [])
      setCustomCSS(customCSS || _customCSS || [])
      setEnabledFeatures(features || [])
      // Save this config and use it in the application
      setMainConfig(mergedMainConfig)
      setIsConfigUpdated(true)
    }
  }, [
    config,
    viewsConfig,
    chatbotData,
    chatbotDataWidget,
    agentData,
    ponyfillFactoryCredentials,
    customVoice,
    colors,
    skipLoader,
    defaultMode,
    GDPR,
    customScripts,
    addressData,
    externalEventHandler,
    externalComponents,
    userData,
    width,
    height,
    menuConfig,
    fetchedConfig
  ])

  return mainConfig ? (
    <RetorikContainer parent={parent} {...mainConfig} />
  ) : (
    <Loader
      className={isConfigUpdated ? 'rf-animate-loaderFadeOut' : ''}
      parent={parent}
      width={width}
      height={height}
      fullSize={!!(parent !== ContainerParent.widget && config?.fullSize !== false)}
    />
  )
}

export default RetorikConfigHandler
